import React, { useEffect, useState } from "react";
import { Formik } from "formik";
import { useI18next } from "../../../../plugins/gatsby-plugin-ap-i18next/src/useI18next";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "@reach/router";
import * as Yup from "yup";

import { container, draw, fieldset, form, start } from "./generator.module.scss";
import {
    EGeneratorFilterKeys,
    IGeneratorFormValues,
    IGeneratorProductsSetPayload,
    TGeneratorFilters,
} from "../../../models/generator.model";
import { TStatus } from "../../../models/status.model";
import {
    selectGeneratorProducts,
    selectGeneratorProductsStatus,
} from "../../../redux/generator-products/generator-products.selectors";
import {
    clearGeneratorFiltersAction,
    getGeneratorFiltersAction,
} from "../../../redux/generator-filters/generator-filters.actions";
import {
    selectGeneratorFilters,
    selectGeneratorFiltersStatus,
} from "../../../redux/generator-filters/generator-filters.selectors";
import {
    clearGeneratorProductsAction,
    getGeneratorProductsAction,
} from "../../../redux/generator-products/generator-products.actions";
import { createSingleStylizationAction } from "../../../redux/single-stylization/single-stylization.actions";
import { selectSingleStylizationStatus } from "../../../redux/single-stylization/single-stylization.selectors";

import FormikForm from "../../../components/hoc/formik-form";
import GeneratorStartFields from "../organisms/generator-start-fields";
import GeneratorDrawFields from "../organisms/generator-draw-fields";
import Loader from "../../../components/atoms/loader";
import useUser from "../../../apps/auth/hooks/use-user";

interface IGeneratorLocationState {
    isSurveySuccessRedirect?: boolean;
}

const Generator: React.FC = () => {
    const { t } = useI18next();
    const location = useLocation();
    const locationState = location.state as IGeneratorLocationState | undefined;
    const isSurveySuccessRedirect = locationState?.isSurveySuccessRedirect;
    const initialName = isSurveySuccessRedirect ? t("generator.stylization.first") : "";
    const dispatch = useDispatch();
    const filters: TGeneratorFilters = useSelector(selectGeneratorFilters);
    const filtersStatus: TStatus = useSelector(selectGeneratorFiltersStatus);
    const { sessionId, sessionGuid, items }: IGeneratorProductsSetPayload = useSelector(selectGeneratorProducts);
    const productsStatus: TStatus = useSelector(selectGeneratorProductsStatus);
    const stylizationStatus: TStatus = useSelector(selectSingleStylizationStatus);
    const [initialFormValues, setInitialFormValues] = useState(
        getFormValues({ ...initialValues, name: initialName }, filters)
    );

    const isLoading = getLoadingStatus(filtersStatus, productsStatus, stylizationStatus, sessionId);
    const [counterClick, setCounterClick] = useState(1);
    const { isLoggedIn } = useUser();

    const handleSubmit = (values: IGeneratorFormValues) => {
        console.log('handleSubmit => values', values);
        dispatch(getGeneratorProductsAction(values));
        setCounterClick((prev) => prev + 1);
        //window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSave = (values: IGeneratorFormValues) => () => {
        dispatch(
            createSingleStylizationAction({
                name: values.name,
                guid: sessionGuid,
                //productIds: items.map((product) => product.productId),
                occasionId: values[EGeneratorFilterKeys.occasion],
            })
        );
    };

    useEffect(() => {
        dispatch(getGeneratorProductsAction(initialFormValues));
        setCounterClick((prev) => prev + 1);
    }, []);

    useEffect(() => {
        dispatch(getGeneratorFiltersAction());
        return () => {
            dispatch(clearGeneratorFiltersAction());
            dispatch(clearGeneratorProductsAction());
        };
    }, [dispatch]);

    useEffect(() => {
        // console.log("[generator]filters", filters);
        const currentSeason = getSeasonFromDate();
        // console.log("currentSeason", currentSeason);
        const seasonLabels = {
            wiosna: "Wiosna",
            lato: "Lato",
            jesien: "Jesień",
            zima: "Zima"
        };
        //filters.seasons = ["wiosna", "lato", "jesien", "zima"].map((value) => ({ value: value, label: value, applied: value === currentSeason }));

        const filtersSeasons = Object.entries(seasonLabels).map(([value, label]) => ({
            value,
            label,
            applied: value === currentSeason
        }));
        filters.seasons = filtersSeasons;
        // console.log("[generator]filters 2", filters);
        setInitialFormValues(getFormValues({ ...initialValues, name: initialName }, filters));
    }, [filters]);

    useEffect(() => {
        window.scrollTo({ top: 0, behavior: "smooth" });
    }, [productsStatus]);


    const [loadingStylizationsListTexts] = useState([
        "Czaruje dla Ciebie...",
        "Już za chwilkę wyczaruje..."
    ]);

    return (
        <div className={container}>
            {isLoading && <Loader position="fixed" texts={loadingStylizationsListTexts} />}
            <Formik
                onSubmit={handleSubmit}
                initialValues={initialFormValues}
                enableReinitialize={true}
                validationSchema={isLoggedIn ? getValidationSchema(t) : null}
            >
                {(formik) => (
                    <FormikForm
                        formik={formik}
                        successMessage=""
                        className={form}
                        fieldsetClassName={fieldset}
                        loaderType="none"
                    >
                        {sessionId || !isLoggedIn ? (
                            <GeneratorDrawFields
                                className={draw}
                                filters={filters}
                                products={items}
                                onSpaceKeydown={formik.submitForm}
                                onSave={handleSave(formik.values)}
                                counterClick={counterClick}
                            />
                        ) : (
                            <GeneratorStartFields className={start} filters={filters} />
                        )}
                    </FormikForm>
                )}
            </Formik>
        </div>
    );
};

function getSeasonFromDate(date: Date = new Date()): string {
    const month = date.getMonth() + 1; // getMonth() zwraca 0–11
    //   console.log("Miesiąc:", month);

    if (month >= 3 && month <= 5) return "wiosna";  // marzec–maj
    if (month >= 6 && month <= 8) return "lato";    // czerwiec–sierpień
    if (month >= 9 && month <= 11) return "jesien"; // wrzesień–listopad
    return "zima";                                  // grudzień–luty
}

const initialValues: IGeneratorFormValues = {
    name: "",
    blockedProducts: [],
    [EGeneratorFilterKeys.colors]: [],
    [EGeneratorFilterKeys.patterns]: [],
    [EGeneratorFilterKeys.occasion]: "",
    [EGeneratorFilterKeys.style]: "",
    [EGeneratorFilterKeys.priceTop]: "",
    [EGeneratorFilterKeys.priceBottom]: "",
    [EGeneratorFilterKeys.priceOuterwear]: "",
    [EGeneratorFilterKeys.priceOverall]: "",
    [EGeneratorFilterKeys.seasons]: "",
};

function getValidationSchema(t: ReturnType<typeof useI18next>["t"]) {
    
    return Yup.object({
        name: Yup.string().required(t("form.validation.required")),
        [EGeneratorFilterKeys.occasion]: Yup.string().required(t("form.validation.required")),
        [EGeneratorFilterKeys.style]: Yup.string().required(t("form.validation.required")),
        [EGeneratorFilterKeys.priceTop]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
        [EGeneratorFilterKeys.priceBottom]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
        [EGeneratorFilterKeys.priceOuterwear]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
        [EGeneratorFilterKeys.priceOverall]: Yup.lazy((value) =>
            typeof value === "number"
                ? Yup.number().required(t("form.validation.required"))
                : Yup.string().required(t("form.validation.required"))
        ),
    });
}

type TValuesFromFilters = Partial<
    Record<keyof IGeneratorFormValues, string | number | string[] | number[]>
>;

function getFormValues(
    initialValues: IGeneratorFormValues,
    filters: TGeneratorFilters
): IGeneratorFormValues {
    const keys = Object.keys(filters) as Array<keyof TGeneratorFilters>;
    const values = keys.reduce((result: TValuesFromFilters, key) => {
        const appliedOptions = filters[key].filter((option) => option.applied);
        const isArray = Array.isArray(initialValues[key]);
        let keyValue;
        if (isArray) {
            keyValue = appliedOptions
                .map((option) => {
                    return option.value || option.id;
                })
                .filter((value) => !!value) as string[] | number[];
        } else {
            keyValue = appliedOptions[0]?.value || appliedOptions[0]?.id;
        }
        if (keyValue) {
            result[key] = keyValue;
        }
        return result;
    }, {});
    return {
        ...initialValues,
        ...values,
    } as IGeneratorFormValues;
}

function getLoadingStatus(
    filtersStatus: TStatus,
    productsStatus: TStatus,
    stylizationStatus: TStatus,
    sessionId: number | null
): boolean {
    if (!filtersStatus || filtersStatus === "idle" || filtersStatus === "loading") return true;
    if (productsStatus === "loading") return true;
    return stylizationStatus === "loading";
}

export default Generator;
